import React from 'react';

const sections = [
  {
    title: '1. Introduction',
    content: `SALESPAD PTE. LTD. ("SalesPad", "we", "our", or "us"), a company incorporated in Singapore, is committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered sales automation platform (the "Service"). Please read this policy carefully. By using our Service, you agree to the practices described herein.`,
  },
  {
    title: '2. Data Controller',
    content: `The data controller for your personal information is SALESPAD PTE. LTD., registered in Singapore. If you have any questions about how we handle your data, please contact us at privacy@salespad.app.`,
  },
  {
    title: '3. Information We Collect',
    content: `We collect information you provide directly, including: account registration details (name, email address, company name, phone number); billing and payment information; communications you send us; and preferences and settings you configure within the Service. We also collect data automatically when you use our Service, such as IP addresses, browser type, device identifiers, usage logs, clickstream data, and cookies. Additionally, our AI agents may process contact data you provide or import (such as prospect names, emails, LinkedIn URLs, and phone numbers) solely for the purpose of executing your automated outreach campaigns.`,
  },
  {
    title: '4. How We Use Your Information',
    content: `We use your personal data to: provide, operate, and improve the Service; process transactions and send billing notices; authenticate users and maintain account security; execute AI-driven outreach campaigns on your behalf; send product updates, newsletters, and promotional communications (with your consent where required); respond to support requests; comply with legal obligations; and conduct analytics to understand usage patterns and improve our platform.`,
  },
  {
    title: '5. Legal Basis for Processing (GDPR)',
    content: `If you are located in the European Economic Area (EEA) or United Kingdom, our legal bases for processing your personal data include: performance of a contract (to deliver the Service you have subscribed to); legitimate interests (improving the Service, fraud prevention, and security); compliance with legal obligations; and your consent (where we rely on it, you may withdraw at any time without affecting prior processing).`,
  },
  {
    title: '6. Sharing of Information',
    content: `We do not sell your personal data. We may share your information with: service providers who assist us in operating the Service (e.g., cloud hosting, payment processors, email delivery), bound by confidentiality obligations; business partners with your explicit consent; law enforcement or government authorities when required by applicable law; and successors in the event of a merger, acquisition, or sale of assets, subject to equivalent protections. We require all third parties to respect the security of your personal data and to treat it in accordance with the law.`,
  },
  {
    title: '7. International Data Transfers',
    content: `Your data may be transferred to and processed in countries other than Singapore, including countries in the EEA, the United States, and other jurisdictions. We ensure that such transfers are subject to appropriate safeguards, such as Standard Contractual Clauses approved by the European Commission, or other mechanisms permitted under applicable law.`,
  },
  {
    title: '8. Data Retention',
    content: `We retain your personal data for as long as your account is active or as needed to provide the Service. We will also retain and use your data as necessary to comply with legal obligations, resolve disputes, and enforce our agreements. Campaign data and contact records are retained for the duration of your subscription and deleted within 90 days of account termination, unless a longer retention period is required by law.`,
  },
  {
    title: '9. Security',
    content: `We implement industry-standard technical and organisational measures to protect your personal data against unauthorised access, alteration, disclosure, or destruction. These include encryption in transit (TLS) and at rest, access controls, regular security assessments, and staff training. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.`,
  },
  {
    title: '10. Your Rights',
    content: `Depending on your jurisdiction, you may have the right to: access the personal data we hold about you; correct inaccurate or incomplete data; request deletion of your data ("right to be forgotten"); restrict or object to our processing; receive your data in a portable format; and withdraw consent at any time where processing is based on consent. To exercise any of these rights, please contact us at privacy@salespad.app. We will respond within the timeframe required by applicable law (generally 30 days).`,
  },
  {
    title: '11. Cookies and Tracking Technologies',
    content: `We use cookies and similar tracking technologies to operate and improve the Service, remember your preferences, and analyse usage. You can control cookie settings through your browser. Note that disabling certain cookies may affect the functionality of the Service. We do not use tracking technologies for cross-site behavioural advertising without your consent.`,
  },
  {
    title: '12. Children\'s Privacy',
    content: `Our Service is not directed to individuals under the age of 18. We do not knowingly collect personal data from children. If you believe we have inadvertently collected such information, please contact us immediately at privacy@salespad.app and we will take steps to delete it.`,
  },
  {
    title: '13. Third-Party Links',
    content: `The Service may contain links to third-party websites or integrations (e.g., LinkedIn, WhatsApp, Google Sheets). This Privacy Policy does not apply to those external services. We encourage you to review the privacy policies of any third-party services you use in connection with SalesPad.`,
  },
  {
    title: '14. Changes to This Policy',
    content: `We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on our website and, where appropriate, by email. The "Last updated" date at the top of this page reflects when the policy was last revised. Your continued use of the Service after changes become effective constitutes your acceptance of the revised policy.`,
  },
  {
    title: '15. Contact Us',
    content: `If you have questions, concerns, or requests relating to this Privacy Policy or our data practices, please contact our Data Protection Officer at:`,
    contact: {
      email: 'privacy@salespad.app',
      company: 'SALESPAD PTE. LTD.',
      country: 'Singapore',
    },
  },
];

const PrivacyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-page-bg" style={{ background: 'radial-gradient(1200px 600px at 75% 45%, rgba(61,139,255,0.10) 0%, rgba(255,86,201,0.07) 35%, rgba(7,15,31,0) 70%), linear-gradient(180deg, #0B1730 0%, #070F1F 100%)' }}>
      <header className="sticky top-0 z-50 border-b border-card-border bg-bg-surface/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center space-x-3">
          <img
            src="/landing-page/salespad logo.svg"
            alt="SalesPad Logo"
            className="w-7 h-7"
          />
          <span className="text-text-primary font-bold text-xl font-inter">SalesPad</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="mb-12">
          <p className="text-sm font-medium text-text-secondary uppercase tracking-widest mb-4">Legal</p>
          <h1 className="text-4xl sm:text-5xl font-black text-text-primary font-inter mb-4 gradient-text">
            Privacy Policy
          </h1>
          <p className="text-text-secondary text-base">
            SALESPAD PTE. LTD. &mdash; Singapore
          </p>
          <p className="text-text-secondary text-sm mt-1">Last updated: April 2025</p>
        </div>

        <div className="space-y-4">
          {sections.map((section, i) => (
            <div
              key={i}
              className="bg-bg-surface border border-card-border rounded-2xl p-6 sm:p-8"
            >
              <h2 className="text-text-primary font-bold text-lg sm:text-xl font-inter mb-3">
                {section.title}
              </h2>
              <p className="text-text-secondary text-sm sm:text-base leading-relaxed">
                {section.content}
              </p>
              {section.contact && (
                <div className="mt-4 space-y-1 text-sm text-text-secondary">
                  <p>{section.contact.company}</p>
                  <p>{section.contact.country}</p>
                  <p>
                    Email:{' '}
                    <a
                      href={`mailto:${section.contact.email}`}
                      className="text-brand-green-teal hover:underline"
                    >
                      {section.contact.email}
                    </a>
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default PrivacyPage;
